// -----------------------------------------------------------
// FloraIQ - CloudClassificationPanel — Aba Classificação da Nuvem
// -----------------------------------------------------------

import { useState } from "react";
import { classificationClasses } from "@/mocks/pointcloud";

export default function CloudClassificationPanel() {
  const [processing, setProcessing] = useState(false);
  const maxPct = Math.max(...classificationClasses.map((c) => c.percentual));

  const handleReprocess = () => {
    setProcessing(true);
    setTimeout(() => setProcessing(false), 2500);
    alert("Simulação: reprocessamento de classificação iniciado (mock)");
  };

  return (
    <div className="space-y-5">
      {/* Gráfico + Card Processamento */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Distribuição por classe */}
        <div className="lg:col-span-2 bg-white rounded-card border border-background-200/70 p-4">
          <h4 className="text-sm font-bold text-foreground-900 font-heading mb-4">Distribuição por Classe</h4>
          <div className="space-y-2">
            {classificationClasses.map((c) => (
              <div key={c.id} className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: c.cor }}></div>
                <span className="text-xs text-foreground-600 w-32 whitespace-nowrap truncate">{c.classe}</span>
                <div className="flex-1 h-4 bg-background-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(c.percentual / maxPct) * 100}%`, backgroundColor: c.cor }}></div>
                </div>
                <span className="text-xs font-medium text-foreground-700 w-10 text-right">{c.percentual}%</span>
                <span className="text-xs text-foreground-400 font-mono w-18 text-right">{c.pontos}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Card Processamento */}
        <div className="bg-white rounded-card border border-background-200/70 p-4">
          <h4 className="text-sm font-bold text-foreground-900 font-heading mb-3">Processamento</h4>
          <div className="space-y-3">
            <div className="flex justify-between text-xs">
              <span className="text-foreground-500">Status</span>
              <span className={`font-medium ${processing ? "text-amber-600" : "text-green-600"}`}>
                {processing ? "Reprocessando..." : "Concluído"}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-foreground-500">Algoritmo</span>
              <span className="text-foreground-700 font-mono">PDAL / CSF + SMRF</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-foreground-500">Tempo estimado</span>
              <span className="text-foreground-700">~18 min</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-foreground-500">Pontos processados</span>
              <span className="text-foreground-700 font-mono">300,0 M</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-foreground-500">Erros</span>
              <span className="text-green-600">0</span>
            </div>
            <button
              onClick={handleReprocess}
              disabled={processing}
              className={`w-full mt-2 text-xs py-2 rounded-md cursor-pointer whitespace-nowrap transition-colors ${
                processing ? "bg-background-100 text-foreground-400 cursor-not-allowed" : "bg-primary-500 text-white hover:bg-primary-600"
              }`}
            >
              <i className={`${processing ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} mr-1`}></i>
              {processing ? "Reprocessando..." : "Reprocessar classificação"}
            </button>
          </div>
        </div>
      </div>

      {/* Tabela de classes */}
      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/70 bg-background-50/50">
                <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Classe</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Cor</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Pontos</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Percentual</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Status</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Ações</th>
              </tr>
            </thead>
            <tbody>
              {classificationClasses.map((c) => (
                <tr key={c.id} className="border-b border-background-100/50 hover:bg-background-50/30 transition-colors">
                  <td className="px-4 py-2.5 text-xs font-medium text-foreground-800">{c.classe}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: c.cor }}></div>
                      <span className="text-xs text-foreground-400 font-mono">{c.cor}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-foreground-600 font-mono">{c.pontos}</td>
                  <td className="px-4 py-2.5 text-xs text-foreground-600">{c.percentual}%</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full border bg-green-50 text-green-700 border-green-200 whitespace-nowrap">Classificado</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer" title="Isolar classe" aria-label="Isolar classe">
                        <i className="ri-focus-3-line text-foreground-500 text-sm"></i>
                      </button>
                      <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer" title="Editar classificação" aria-label="Editar classificação">
                        <i className="ri-edit-line text-foreground-500 text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}